<template>
  <BasicModal
    destroyOnClose
    v-bind="$attrs"
    @register="registerModal"
    @ok="onModalOk"
    @cancel="resetModal"
  >
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>

    <div class="mts-page-container !min-h-unset !m-0 !h-full">
      <SearchTable
        v-show="renderMode === 'MODAL_TABLE'"
        ref="searchTableRef"
        v-bind="bindTableEvents"
      >
        <template v-for="item in Object.keys($slots)" #[item]="data">
          <slot :name="item" v-bind="data || {}"></slot>
        </template>
      </SearchTable>

      <FormWithTabs v-show="renderMode === 'MODAL_FORM'" ref="formTabsRef" v-bind="bindFormEvents">
        <template v-for="item in Object.keys($slots)" #[item]="data">
          <slot :name="item" v-bind="data || {}"></slot>
        </template>
      </FormWithTabs>
    </div>

    <template v-if="asyncTask" #insertFooter>
      <TaskProgress :asyncTask="asyncTask" @retry="onModalOk" />
    </template>
  </BasicModal>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import type { RowSelectionType } from 'ant-design-vue/es/table/interface';
  import { deepMerge, isArray, isBoolean, isPlainObject, message } from '@mts-fe/utils';
  import { TaskProgress } from '@mts/components/Base';
  import { BasicModal, useModalInner } from '@mts/components/Modal';
  import { t } from '@mts/hooks/web/useI18n';
  import { mtsEmitter } from '@mts/utils';
  import { FORM_EMIT_EVENTS, useFormEvents } from '../hooks/useRenderForm';
  import { TABLE_EMIT_EVENTS, useTableEvents } from '../hooks/useRenderTable';
  import {
    ActionContext,
    createSchemaApi,
    FormWithTabsExposed,
    getActionHookFn,
    getActionRoute,
    getAsyncTaskParams,
    getRoutePageMode,
    LIST_PAGE_SCHEMA,
    mergeResponse,
    ModalRenderMode,
    PAGE_ITEM_SCHEMA,
    PageModalPayload,
    parsePageItemSchema,
    parsePageSchema,
    parseRouteMethod,
    SearchTableExposed,
    useSchemaActionContext,
    useSchemaPageContext,
  } from '..';
  import FormWithTabs from './FormWithTabs.vue';
  import SearchTable from './SearchTable.vue';

  defineOptions({
    name: 'BasePageModal',
  });

  const renderMode = ref<ModalRenderMode>();
  const tableSelectType = ref<RowSelectionType>();
  const searchTableRef = ref<SearchTableExposed | null>(null);
  const formTabsRef = ref<FormWithTabsExposed | null>(null);
  const curPayload = ref<Omit<PageModalPayload, 'pageSchema'>>();

  const [registerModal, modalAction] = useModalInner(
    ({ pageSchema, actionEvent, modalHandler, record }: PageModalPayload) => {
      console.log(pageSchema, actionEvent, 'payload', record);
      const { renderMode: mode, modalPayload = {}, ...schema } = pageSchema;
      renderMode.value = mode;
      curPayload.value = { actionEvent, modalHandler, record };
      const {
        searchInfo = {},
        searchType = {},
        filterInfo = {},
        filterType = {},
        formValue = {},
        fieldsDisabled = [],
      } = modalPayload;
      const disableField = (i) => fieldsDisabled.includes(i.field) && (i.dynamicDisabled = true);
      context.payloadFormValue = formValue;

      if (renderMode.value === 'MODAL_TABLE') {
        const { actions = [], ...pageProps } = parsePageSchema(schema as LIST_PAGE_SCHEMA);
        pageProps.formConfig?.schemas?.forEach(disableField);
        console.log('MODAL_TABLE', { ...pageProps }, pageProps.rowSelection?.type);

        tableSelectType.value = pageProps.rowSelection?.type;
        searchTableRef.value?.setProps({
          actions,
          minHeight: window.innerHeight * 0.4,
          ...pageProps,
          searchInfo: deepMerge(searchInfo, pageProps.searchInfo),
          searchType: deepMerge(searchType, pageProps.searchType),
          filterInfo: deepMerge(filterInfo, pageProps.filterInfo),
          filterType: deepMerge(filterType, pageProps.filterType),
        });
      }
      if (renderMode.value === 'MODAL_FORM') {
        const pageMode = getRoutePageMode(actionEvent.eventType) || 'Add';
        const pageProps = parsePageItemSchema(schema as PAGE_ITEM_SCHEMA, { pageMode });

        pageProps.mainForm?.schemas?.forEach(disableField);
        console.log('MODAL_FORM', pageMode, { ...pageProps }, { ...record, ...formValue });
        formTabsRef.value?.setCompProps({
          ...pageProps,
          pageMode,
          searchInfo,
          record: { ...record, ...formValue },
        });
      }
    },
  );

  const asyncTask = ref<Recordable>();
  const actionRef = computed(() => {
    if (renderMode.value === 'MODAL_TABLE') {
      return searchTableRef.value;
    }
    if (renderMode.value === 'MODAL_FORM') {
      return formTabsRef.value;
    }
    return null;
  });

  const emit = defineEmits(['register', 'ok', ...FORM_EMIT_EVENTS, ...TABLE_EMIT_EVENTS]);
  const bindFormEvents = useFormEvents(emit);
  const bindTableEvents = useTableEvents(emit);
  const router = useRouter();
  const pageContext = useSchemaPageContext();
  const actionContext = useSchemaActionContext();
  const context: ActionContext = {
    ...pageContext,
    actionRef,
    message,
    router,
    t,
    createSchemaApi,
    payloadFormValue: {},
    modalAction,
    emit,
  };

  const onModalOk = async () => {
    resetModal();
    modalAction.changeOkLoading(true);
    try {
      let data;

      if (renderMode.value === 'MODAL_TABLE') {
        console.log('onModalOk', searchTableRef.value?.getSelectRows(), curPayload.value);
        const values = searchTableRef.value?.getSelectRows() ?? [];
        switch (tableSelectType.value) {
          case 'checkbox':
            data = values;
            break;
          case 'radio':
            data = values[0] ?? {};
            break;
          default:
            data = searchTableRef.value?.getDataSource() ?? [];
        }
      }
      if (renderMode.value === 'MODAL_FORM') {
        const values = await formTabsRef.value?.validate();
        const pageData = formTabsRef.value?.getPageData();
        data = deepMerge(pageData, values, { mergeUndefined: true });
        console.log('onModalOk', values, pageData, data, curPayload.value);
      }

      const { actionEvent, modalHandler = {}, record } = curPayload.value || {};
      const {
        checkSelect,
        beforeApiFn = actionContext?.beforeApiFn,
        eventApi,
        flatParam,
        modelId,
        afterApiFn = actionContext?.afterApiFn,
        routeUrl,
        silent,
      } = modalHandler;
      Object.assign(context, {
        actionEvent,
        record,
        getDataSelectedRows: (relationKey: string) => {
          const keys = data[relationKey + '@selectedRowKeys'] || [];
          return data[relationKey].filter((i) => keys.includes(i.key));
        },
      });

      if (checkSelect) {
        const isEmpty = !data || isArray(data) ? !data.length : !Object.keys(data).length;
        if (isEmpty) {
          message.open({ type: 'error', content: t('g.25090008'), key: 'selectionEmpty' });
          modalAction.changeOkLoading(false);
          return;
        }
      }

      if (beforeApiFn) {
        const res = await getActionHookFn(beforeApiFn)(data, context, actionEvent?.eventKey);
        if (res !== null && typeof res === 'object') {
          data = res;
        }
        if (typeof res === 'boolean' && !res) {
          modalAction.changeOkLoading(false);
          return;
        }
      }

      if (eventApi) {
        const params = {
          modelId: modelId || actionEvent?.modelId,
          eventKey: actionEvent?.eventKey,
          apiParams: { ...actionEvent?.apiParams },
        };
        Object.assign(params, flatParam || actionEvent?.flatParam ? data : { data });
        actionEvent?.taskCode?.length && Object.assign(params, getAsyncTaskParams(actionEvent));
        const res = await createSchemaApi(eventApi)?.(params);
        if (actionEvent?.taskCode?.length) {
          asyncTask.value = res;
          modalAction.setModalProps({ showCancelBtn: false, showOkBtn: false });
          return;
        }
        if (res && !isBoolean(res)) {
          if (isPlainObject(res)) {
            data = mergeResponse(data, res, !actionEvent?.oldMerge);
          } else if (isArray(res) || typeof res == 'string') {
            data = res;
          } else {
            console.error('未处理的值类型', res);
          }
        }
        !silent && message.success(t('common.opSuccess'));
      }
      modalAction.changeOkLoading(false);
      modalAction.closeModal();

      if (afterApiFn) {
        await getActionHookFn(afterApiFn)(data, context, actionEvent?.eventKey);
      }

      if (routeUrl) {
        nextTick(() => {
          const [path, method] = parseRouteMethod(getActionRoute(routeUrl, data));
          method === 'go' ? router[method](+path) : router[method](path);
        });
        return;
      }
      emit('ok', data, curPayload.value);
    } catch (error) {
      modalAction.changeOkLoading(false);
      console.error(error);
    }
  };

  const resetModal = () => {
    asyncTask.value && mtsEmitter.emit('hideAsyncTask', { ...asyncTask.value });
    asyncTask.value = void 0;
    modalAction.setModalProps({ showCancelBtn: true, showOkBtn: true, confirmLoading: false });
  };

  defineExpose({ ...modalAction, actionRef });
</script>
